// /app/frontend/src/components/dental_intel/FactorCard.jsx
// iter1111 — Primitive components for the DHI factor-summary cards that
// REPLACED the retired 0-100 composite scores. See
// /app/memory/DHI_EVIDENCE_REGISTER.md and /app/design_guidelines.json.
//
// Design intent (non-negotiable):
//   • No numeric score, no gauge, no colour band that reads as a grade.
//   • Each factor shows: status chip + label + educational note + source.
//   • Visual language is "reference material", not "verdict".
//   • Light + dark mode supported via `isDark` prop.

import React, { useState } from "react";
import {
  CheckCircle2, MinusCircle, HelpCircle, AlertTriangle,
  Stethoscope, FileText, ChevronRight, ChevronDown, BookOpen, Info,
} from "lucide-react";

/** Status → chip + icon per /app/design_guidelines.json. */
const STATUS_META = {
  recorded: {
    label_bg: "Записано", label_en: "Recorded",
    icon: CheckCircle2,
    light: "bg-slate-100 text-slate-800 border-slate-300",
    dark:  "bg-slate-800/60 text-slate-200 border-slate-700",
    icon_color: "text-slate-600 dark:text-slate-300",
  },
  present: {
    label_bg: "Наличен фактор", label_en: "Present",
    icon: CheckCircle2,
    light: "bg-teal-50 text-teal-800 border-teal-200",
    dark:  "bg-teal-950/40 text-teal-300 border-teal-800/60",
    icon_color: "text-teal-600 dark:text-teal-400",
  },
  ok: {
    label_bg: "Наред", label_en: "OK",
    icon: CheckCircle2,
    light: "bg-teal-50 text-teal-800 border-teal-200",
    dark:  "bg-teal-950/40 text-teal-300 border-teal-800/60",
    icon_color: "text-teal-600 dark:text-teal-400",
  },
  absent: {
    label_bg: "Не е отбелязано", label_en: "Not reported",
    icon: MinusCircle,
    light: "bg-slate-50 text-slate-700 border-slate-200",
    dark:  "bg-white/[0.04] text-slate-300 border-white/10",
    icon_color: "text-slate-400 dark:text-slate-500",
  },
  unknown: {
    label_bg: "Непознато", label_en: "Unknown",
    icon: HelpCircle,
    light: "bg-slate-50 text-slate-600 border-slate-200",
    dark:  "bg-white/[0.04] text-slate-400 border-white/10",
    icon_color: "text-slate-400 dark:text-slate-500",
  },
  concern: {
    label_bg: "Обсъди със зъболекар", label_en: "Discuss with dentist",
    icon: AlertTriangle,
    light: "bg-amber-50 text-amber-900 border-amber-200",
    dark:  "bg-amber-500/10 text-amber-200 border-amber-500/30",
    icon_color: "text-amber-600 dark:text-amber-400",
  },
  requires_clinician: {
    label_bg: "Изисква клиничен преглед",
    label_en: "Requires clinician examination",
    icon: Stethoscope,
    light: "bg-indigo-50 text-indigo-900 border-indigo-200",
    dark:  "bg-indigo-500/10 text-indigo-200 border-indigo-500/30",
    icon_color: "text-indigo-600 dark:text-indigo-400",
  },
};

/** A single status chip. */
export function StatusChip({ status, isDark = false, lang = "bg", testid }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  const Icon = meta.icon;
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-semibold whitespace-nowrap ${
        isDark ? meta.dark : meta.light
      }`}
    >
      <Icon className={`w-3.5 h-3.5 ${meta.icon_color}`} />
      {lang === "en" ? meta.label_en : meta.label_bg}
    </span>
  );
}

/** Single factor row — the atom of every factor card. */
export function FactorRow({
  label_bg, label_en, note_bg, note_en, source_bg, source_en,
  status, value, lang = "bg", isDark = false, testid,
}) {
  const label = lang === "en" ? (label_en || label_bg) : (label_bg || label_en);
  const note = lang === "en" ? (note_en || note_bg) : (note_bg || note_en);
  const source = lang === "en" ? (source_en || source_bg) : (source_bg || source_en);
  return (
    <li
      data-testid={testid || "dhi-factor-row"}
      className={`py-2.5 px-3 rounded-lg transition-colors ${
        isDark ? "hover:bg-white/[0.03]" : "hover:bg-slate-50"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-semibold ${isDark ? "text-slate-100" : "text-slate-900"}`}>
            {label}
            {value !== undefined && value !== null && (
              <span className={`ml-2 font-mono text-xs ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                {value}
              </span>
            )}
          </div>
          {note && (
            <div className={`mt-1 text-xs leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {note}
            </div>
          )}
          {source && (
            <div className={`mt-1 flex items-center gap-1 text-[11px] font-mono ${
              isDark ? "text-slate-500" : "text-slate-500"
            }`}>
              <BookOpen className="w-3 h-3" />
              <span className="truncate">{source}</span>
            </div>
          )}
        </div>
        {status && (
          <div className="shrink-0 pt-0.5">
            <StatusChip status={status} isDark={isDark} lang={lang} />
          </div>
        )}
      </div>
    </li>
  );
}

/** Legacy badge — shown on historical snapshots. Patient-friendly wording. */
export function LegacyBadge({ lang = "bg", isDark = false }) {
  const txt = lang === "en"
    ? "Older snapshot — kept for your reference"
    : "По-стара снимка — запазена за справка";
  return (
    <span
      data-testid="dhi-legacy-badge"
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${
        isDark
          ? "bg-rose-500/10 text-rose-200 border-rose-500/30"
          : "bg-rose-50 text-rose-900 border-rose-200"
      }`}
    >
      <Info className="w-3 h-3" />
      {txt}
    </span>
  );
}

/** Educational "retired score" banner on each card. */
export function RetiredExplainer({ reason, isDark = false }) {
  if (!reason) return null;
  return (
    <div
      className={`text-xs leading-relaxed rounded-lg px-3 py-2 ${
        isDark ? "bg-white/[0.03] text-slate-300 border border-white/10"
               : "bg-slate-50 text-slate-700 border border-slate-200"
      }`}
    >
      <div className="flex items-start gap-2">
        <FileText className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${
          isDark ? "text-slate-400" : "text-slate-500"
        }`} />
        <span>{reason}</span>
      </div>
    </div>
  );
}

/** "Not assessable — add data" state. */
export function NotAssessableState({
  reason, missing = [], ctaOnClick, lang = "bg", isDark = false,
}) {
  const cta = lang === "en" ? "Add this factor to complete your record"
                            : "Добавете този фактор за по-пълна картина";
  return (
    <div
      data-testid="dhi-not-assessable"
      className={`rounded-xl border-l-4 p-4 ${
        isDark
          ? "bg-indigo-500/5 border-indigo-500/60 border-y border-r border-indigo-500/20"
          : "bg-indigo-50/70 border-indigo-500 border border-indigo-200"
      }`}
    >
      <div className={`text-xs leading-relaxed ${isDark ? "text-indigo-100" : "text-indigo-900"}`}>
        {reason}
      </div>
      {missing && missing.length > 0 && (
        <div className={`mt-2 text-[11px] font-mono ${isDark ? "text-indigo-300" : "text-indigo-700"}`}>
          {lang === "en" ? "Missing:" : "Липсва:"} {missing.join(", ")}
        </div>
      )}
      {ctaOnClick && (
        <button
          type="button"
          onClick={ctaOnClick}
          data-testid="dhi-not-assessable-cta"
          className={`mt-3 inline-flex items-center gap-1 text-xs font-semibold ${
            isDark ? "text-indigo-200 hover:text-indigo-100"
                   : "text-indigo-700 hover:text-indigo-900"
          }`}
        >
          {cta} <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

/** The outer card shell for every retired-score section. */
export function FactorCard({
  title, subtitle, icon: Icon, children, isDark = false,
  testid, defaultOpen = true, source_summary,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      data-testid={testid}
      className={`rounded-2xl border p-4 sm:p-5 transition-colors ${
        isDark
          ? "bg-slate-950/40 border-white/10"
          : "bg-white border-slate-200"
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-3 text-left group"
      >
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
              isDark ? "bg-white/[0.04] text-slate-300" : "bg-slate-100 text-slate-700"
            }`}>
              <Icon className="w-4.5 h-4.5" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className={`text-base sm:text-lg font-bold tracking-tight ${
              isDark ? "text-slate-100" : "text-slate-900"
            }`}>
              {title}
            </h3>
            {subtitle && (
              <div className={`mt-0.5 text-xs ${
                isDark ? "text-slate-400" : "text-slate-600"
              }`}>
                {subtitle}
              </div>
            )}
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          } ${isDark ? "text-slate-400" : "text-slate-500"}`}
        />
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {children}
          {source_summary && (
            <div className={`text-[10px] uppercase font-bold tracking-wider pt-2 border-t ${
              isDark ? "text-slate-500 border-white/5" : "text-slate-400 border-slate-100"
            }`}>
              {source_summary}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
